import { Form, Spin, message, DatePicker, Button, Card} from 'antd';
import { FilePdfOutlined } from '@ant-design/icons';
import { pdf } from '@react-pdf/renderer';
import WebLinks from '../../api/WebLinks';
import {useContext, useEffect, useRef, useState } from 'react';
import constants from '../../constants/constants';
import Cookies from 'js-cookie';
import dayjs from 'dayjs';
import debounce from 'lodash/debounce';
import { fetchFiles, trackPageAccess } from '../../globalFunctions/GlobalFunctions';
import { CommonContext } from '../../Context';
import AnalysisSummary from './AnalysisSummary';
import AnalysisSummaryPdf from './AnalysisSummaryPdf';
import AnalysisMasterSummary from './AnalysisMasterSummary';
import AnalysisMasterSummaryPdf from './AnalysisMasterSummaryPdf';

const { RangePicker } = DatePicker;
const reportGenerationSteps = [
  "📥 Gathering your application and job data...",
  "📊 Analysing your data...",
  "🔍 Reviewing quality pass rates and funnel performance...",
  "📍 Identifying top occupations and geographic trends...",
  "🧠 Building your report...",
  "✨ Adding the finishing touches...",
];
const ReportGenerationProgress = ({ activeStep }) => (
  <div className="report-generation-progress" role="status" aria-live="polite">
    <div className="report-generation-progress__title">Please wait...</div>
    <div className="report-generation-progress__bar" aria-hidden="true">
      <span />
    </div>
    <div className="report-generation-progress__steps">
      {reportGenerationSteps.map((step, index) => (
        <div
          className={`report-generation-progress__step ${
            index < activeStep ? "is-complete" : ""
          } ${index === activeStep ? "is-active" : ""}`}
          key={step}
        >
          <span className="report-generation-progress__marker" aria-hidden="true">
            {index < activeStep ? "✓" : index + 1}
          </span>
          <span>{step}</span>
        </div>
      ))}
    </div>
  </div>
);

const Analysis = (props) => {
	//Variable Declaration
	const [form] = Form.useForm();
  const [rangePickerValue, setRangePickerValue] = useState([]);
  // const [loading, setLoading] = useState(false);
  const [analysisLoader, setAnalysisLoader] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [activeProgressStep, setActiveProgressStep] = useState(0);
  const commonContext=useContext(CommonContext);
  const buyerId = commonContext.common.buyerId;
  const [analysisInsights, setAnalysisInsights] = useState(null);
  const [analysisReportId, setAnalysisReportId] = useState(null);
  const iframeRef = useRef(null);
  const masterBuyerId = sessionStorage.getItem('isMasterBuyer');
  const isMasterBuyer =
    masterBuyerId && masterBuyerId !== 'undefined' && masterBuyerId !== 'null';

  useEffect(() => {
    if (!analysisLoader) {
      setActiveProgressStep(0);
      return undefined;
    }
    const progressTimer = setInterval(() => {
      setActiveProgressStep((currentStep) => {
        return Math.min(currentStep + 1, reportGenerationSteps.length - 1);
      });
    }, 4000);

    return () => clearInterval(progressTimer);
  }, [analysisLoader]);

	//Function runs when range picker value is changed
	const RangePickerChanged = debounce((_, dateStrings) => {
		setRangePickerValue([
			dayjs(dateStrings[0], constants.DateFormat2),
			dayjs(dateStrings[1], constants.DateFormat2),
		]);		
	}, 500);

  useEffect(() => {
    trackPageAccess(buyerId,window.location.href)
    return () => {
      if (iframeRef.current?._reportResizeObserver) {
        iframeRef.current._reportResizeObserver.disconnect();
        iframeRef.current._reportResizeObserver = null;
      }
    };
  }, []);

  const generateSummary = async (values) => {
    const range = values?.rangePicker || rangePickerValue;
    if (!range || range.length === 0) {
      message.error("Please select the date to generate report.");
      return;
    }
    const fromDate = rangePickerValue[0].format(constants.DateFormat2);
    const toDate = rangePickerValue[1].format(constants.DateFormat2);
    try {
      const excelBlob = await fetchFiles((
        isMasterBuyer ? WebLinks.GenerateMasterExecutiveSummaryExcel +
          masterBuyerId + "&fromDate=" + fromDate + "&toDate=" + toDate : WebLinks.GenerateExecutiveSummaryExcel +
        buyerId + "&fromDate=" + fromDate + "&toDate=" + toDate
      ));
      return excelBlob;
    } catch (_error) {
      message.error("Failed to generate report.");
      setAnalysisLoader(false);
      return null;
    }
  };

  const generateNewAnalysis = async (values,excelFile) => {
    if (!excelFile) {
      message.error(constants.ERROR_MESSAGE);
      return null;
    }
    const formData = new FormData();
    const fromDate = rangePickerValue[0].format(constants.DateFormat2);
    const toDate = rangePickerValue[1].format(constants.DateFormat2);
    formData.append("fromDate", fromDate);
    formData.append("toDate", toDate);
    formData.append("buyerId", buyerId);
    formData.append("excelFile", excelFile);
    let apiUrl = isMasterBuyer
      ? WebLinks.GenerateMasterAnalysisInsights
      : WebLinks.GenerateAnalysisInsights;
    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          Authorization: "Bearer " + Cookies.get(constants.COOKIE_TOKEN),
        },
        body: formData,
      });
      const result = await response.json();
      if (!response.ok) {
        message.error(result?.message || constants.ERROR_MESSAGE);
        return null;
      }
      message.success(result?.message || "Analysis generated successfully.");
      setAnalysisInsights(result?.data);
      setAnalysisReportId(result?.analysisReportId ?? null);
      return result;
    } catch (error) {
      message.error(constants.ERROR_MESSAGE);
      setAnalysisLoader(false);
      return null;
    }
  };

  // const getHtmlContent = (data) => {
  //   if (!data) return "";

  //   // If API unexpectedly returns an object
  //   if (typeof data !== "string") {
  //     console.error("Expected HTML string, received:");
  //     return "";
  //   }

  //   let html = data.trim();
  //   html = html
  //     .replace(/^```(?:html)?\s*/i, "")
  //     .replace(/\s*```$/i, "")
  //     .trim();
  //   return html;
  // };

  const handleGenerateReport = async (values) => {
    setAnalysisLoader(true);
    try {
      // 1. Generate the latest Excel
      const excel = await generateSummary(values);
      if (!excel) return;

      // 2. Pass the newly generated Excel directly
      const analysisResult = await generateNewAnalysis(values, excel);
      if (!analysisResult) return;

    } finally {
      setAnalysisLoader(false);
    }
  };
  const disabledRangeDate = (current) => {
    if (!current) return false;

    const today = dayjs().endOf("day");
    const twoMonthsAgo = dayjs().subtract(2, "month").startOf("day");
    return (
      current.isAfter(today, "day") ||
      current.isAfter(twoMonthsAgo, "day")
    );
  };

  // const handleIframeLoad = () => {
  //   const iframe = iframeRef.current;
  //   if (!iframe) return;

  //   const doc = iframe.contentDocument;
  //   if (!doc) return;

  //   const updateHeight = () => {
  //     const body = doc.body;
  //     const html = doc.documentElement;
  //     if (!body || !html) return;

  //     const height = Math.max(
  //       body.scrollHeight,
  //       body.offsetHeight,
  //       html.scrollHeight,
  //       html.offsetHeight
  //     );
  //     iframe.style.height = `${height}px`;
  //   };
  //   // First measurement
  //   updateHeight();
  //   // Wait for browser layout/repaint
  //   requestAnimationFrame(() => {
  //     updateHeight();
  //     requestAnimationFrame(() => {
  //       updateHeight();
  //     });
  //   });
  //   // Handle delayed resources/layout
  //   setTimeout(updateHeight, 100);
  //   setTimeout(updateHeight, 300);
  //   setTimeout(updateHeight, 600);
  //   setTimeout(updateHeight, 1000);
  //   // Handle images that haven't finished loading
  //   Array.from(doc.images).forEach((image) => {
  //     if (!image.complete) {
  //       image.addEventListener("load", updateHeight, { once: true });
  //       image.addEventListener("error", updateHeight, { once: true });
  //     }
  //   });
  // };
  const getParsedAnalysis = (analysisInsights) => {
    if (!analysisInsights) return null;

    try {
      return JSON.parse(
        analysisInsights
          .replace(/^```json\s*/, "")
          .replace(/```$/, "")
          .trim()
      );
    } catch (e) {
      console.error("Invalid data", e);
      return null;
    }
  };

  const sanitizeFileNamePart = (value) =>
    String(value || "")
      .replace(/[<>:"/\\|?*\u0000-\u001F]/g, "-")
      .replace(/\s+/g, " ")
      .trim();

  const handleDownloadPdf = async () => {
    const report = getParsedAnalysis(analysisInsights);
    if (!report) {
      message.error("Generate a valid report before exporting to PDF.");
      return;
    }

    const selectedRange = rangePickerValue.length === 2
      ? rangePickerValue
      : form.getFieldValue("rangePicker");
    const datePeriod = Array.isArray(selectedRange) && selectedRange.length === 2
      ? `${dayjs(selectedRange[0]).format("YYYY-MM-DD")} to ${dayjs(selectedRange[1]).format("YYYY-MM-DD")}`
      : report?.meta?.date_range_display || "Date range unavailable";
    const buyerName = sanitizeFileNamePart(report?.meta?.buyer_name || "Buyer");
    const safeDatePeriod = sanitizeFileNamePart(datePeriod);
    const reportIdPart = analysisReportId ? `${analysisReportId} - ` : "";
    const fileName = `Executive Summary - ${reportIdPart}${buyerName} - ${safeDatePeriod}.pdf`;

    setPdfLoading(true);
    try {
      const blob = await pdf(
        isMasterBuyer
          ? <AnalysisMasterSummaryPdf report={report} />
          : <AnalysisSummaryPdf report={report} />
      ).toBlob();
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(downloadUrl), 1000);
      message.success("PDF downloaded successfully.");
    } catch (error) {
      console.error("Failed to generate executive summary PDF:", error);
      message.error("Failed to generate PDF.");
    } finally {
      setPdfLoading(false);
    }
  };

	return (
		<div>
      <Spin
        spinning={analysisLoader}
        indicator={<></>}
        tip={<ReportGenerationProgress activeStep={activeProgressStep} />}
      >
			<div>
				<div className="w-full">
          <Form form={form} onFinish={handleGenerateReport}>
            <div className="flex flex-col md:flex-row md:items-center gap-3 mb-1">
              <Form.Item
                name="rangePicker"
                className="mb-0"
                rules={[
                  {
                    required: true,
                    message: "Required field",
                  },
                ]}
              >
                <RangePicker
                  className="w-full md:w-[306px]"
                  placeholder={["From", "To"]}
                  onChange={RangePickerChanged}
                  format={constants.DateFormat2}
                  disabledDate={disabledRangeDate}
                  order={false}
                  defaultPickerValue={dayjs().subtract(3, "month").startOf("month")}
                />
              </Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                className="w-auto self-start"
              >
                Generate Report
              </Button>
              <Button
                type="primary"
                htmlType="button"
                icon={<FilePdfOutlined />}
                onClick={handleDownloadPdf}
                loading={pdfLoading}
                disabled={!analysisInsights}
                className="w-auto self-start"
              >
                Export to PDF
              </Button>
            </div>
					</Form>
				</div>
        {/* {analysisInsights ? (
          <Card
            className="mt-4 shadow-sm rounded-lg"
            styles={{ body: { padding: 0 } }}
          >
              <iframe
                ref={iframeRef}
                srcDoc={getHtmlContent(analysisInsights)}
                title="Analysis Report"
                className="w-full border-0"
                style={{
                  width: "100%",
                  height: "1px",
                  display: "block",
                  overflow: "hidden",
                }}
                onLoad={handleIframeLoad}
              />
          </Card>
        ) : null} */}
          {analysisInsights ? (
            <Card className="mt-4 shadow-sm rounded-lg">
              <div className="flex flex-col items-start space-y-3">
                <div className="whitespace-pre-wrap w-full" ref={iframeRef}>
                  {analysisInsights ? (
                    isMasterBuyer ? (
                      <AnalysisMasterSummary report={getParsedAnalysis(analysisInsights)} />
                    ) : (
                      <AnalysisSummary report={getParsedAnalysis(analysisInsights)} />
                    )
                  ) : (
                    "Your insights will appear here after you generate the insights."
                  )}
                </div>
              </div>
            </Card>
          ) : null}
			</div>
      </Spin>
		</div>
	);
};

export default Analysis;
